import type { HoroscopeScope } from './domain.js';
import type { Feature } from './rules.js';

export interface EvidenceIndexEntry {
  evidenceRef: string;
  featureIds: readonly string[];
  kinds: readonly string[];
  scopes: readonly HoroscopeScope[];
  evidenceGroups: readonly string[];
}

export type EvidenceIndex = ReadonlyMap<string, EvidenceIndexEntry>;

function uniqueSorted(values: readonly string[]): string[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}

export function buildEvidenceIndex(features: readonly Feature[]): EvidenceIndex {
  const mutable = new Map<string, { featureIds: string[]; kinds: string[]; scopes: HoroscopeScope[]; evidenceGroups: string[] }>();
  for (const feature of features) {
    for (const evidenceRef of feature.evidenceRefs) {
      const bucket = mutable.get(evidenceRef) ?? { featureIds: [], kinds: [], scopes: [], evidenceGroups: [] };
      bucket.featureIds.push(feature.id);
      bucket.kinds.push(feature.kind);
      bucket.scopes.push(feature.scope);
      bucket.evidenceGroups.push(feature.evidenceGroup);
      mutable.set(evidenceRef, bucket);
    }
  }

  return new Map(
    [...mutable.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([evidenceRef, bucket]) => [
        evidenceRef,
        Object.freeze({
          evidenceRef,
          featureIds: Object.freeze(uniqueSorted(bucket.featureIds)),
          kinds: Object.freeze(uniqueSorted(bucket.kinds)),
          scopes: Object.freeze(uniqueSorted(bucket.scopes) as HoroscopeScope[]),
          evidenceGroups: Object.freeze(uniqueSorted(bucket.evidenceGroups)),
        }),
      ] as const),
  );
}

export function selectEvidenceByGroup(index: EvidenceIndex, group: string): readonly EvidenceIndexEntry[] {
  return [...index.values()]
    .filter((entry) => entry.evidenceGroups.includes(group))
    .sort((a, b) => a.evidenceRef.localeCompare(b.evidenceRef));
}

export function resolveEvidenceRefs(index: EvidenceIndex, evidenceRefs: readonly string[]): readonly EvidenceIndexEntry[] {
  return uniqueSorted(evidenceRefs)
    .map((ref) => index.get(ref))
    .filter((entry): entry is EvidenceIndexEntry => Boolean(entry));
}
